import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { Option } from 'components/Forms';
import { FormData, getLocalizedSchema } from 'domains/client/ProfileSelectForm/schema';
import { Button, Paragraph, RadioGroup, SubmitButton } from '@expressable/syntax-ds';
import { yupResolver } from '@hookform/resolvers/yup';
import config from 'config';

type Props = {
  initialData: Partial<FormData>;
  onSubmit: SubmitHandler<FormData>;
  clientOptions: Option[];
};

export const ProfileSelectForm = ({ initialData, clientOptions, onSubmit }: Props): JSX.Element => {
  const { t } = useTranslation();

  const methods = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(getLocalizedSchema(t)),
    defaultValues: {
      clientID: initialData.clientID,
    } as FormData,
  });
  const { handleSubmit } = methods;
  const supportEmail = config.get('supportEmail');

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col items-center gap-e32">
        <RadioGroup name="clientID" data-testid="profileSelect">
          {clientOptions.map(option => (
            <RadioGroup.Option key={option.value} value={option.value} variant="card" id={`clientID-${option.value}`}>
              {option.label as string}
            </RadioGroup.Option>
          ))}
        </RadioGroup>

        <SubmitButton size="lg" fullWidth>
          {t('profileSelect.form.submitButtonText')}
        </SubmitButton>

        <Paragraph className="grid grid-flow-col gap-4">
          {t('profileSelect.form.helpText')}{' '}
          <Button
            variant="link"
            data-testid="form-help-text-button"
            onClick={() => window.open(`mailto:${supportEmail}`, '_blank')}
            fullWidth={false}
            size="lg"
          >
            {t('profileSelect.form.helpTextLink')}
          </Button>
        </Paragraph>
      </form>
    </FormProvider>
  );
};
